import { useCallback, useEffect, useMemo, useState } from "react";
import * as aiService from "../../services/aiAssistantService";

// What the Channels dashboard shows about the AI Assistant, as two small hooks. Both fail quietly: when the
// AI is not installed, or the vendor has no AI settings, they give "nothing" and the dashboard looks exactly
// as it did before.

// The AI's view of the open chat: who has it, the handoff note, the draft waiting for staff, and which
// messages the assistant wrote. Reloaded when `refreshKey` changes (the dashboard passes something that
// changes whenever the thread does), and on demand.
export function useAiChatInfo(customerId, refreshKey) {
  const [info, setInfo] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!customerId) {
      setInfo(null);
      return undefined;
    }
    let cancelled = false;
    aiService.getChatInfo(customerId).then((data) => {
      if (!cancelled) setInfo(data);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, refreshKey, reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const aiMessageIds = useMemo(() => new Set(((info && info.ai_message_ids) || []).map(Number)), [info]);
  return { info, refresh, aiMessageIds };
}

const BADGE_REFRESH_MS = 30000;

// customer_id -> { ai_state, handoff_reason, language, has_draft } for the chats in the list. Fetched again
// when a chat's last message changes, and every 30 s (a handoff does not always move the list).
export function useAiBadges(chatUsers) {
  const [badges, setBadges] = useState({});
  const [tick, setTick] = useState(0);

  const key = (chatUsers || [])
    .filter((u) => u && u.customer_id)
    .slice(0, 200)
    .map((u) => `${u.customer_id}:${u.created_at || ""}`)
    .join(",");

  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), BADGE_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    const ids = key.split(",").map((pair) => pair.split(":")[0]);
    aiService.getChatBadges(ids).then((result) => {
      if (!cancelled) setBadges(result || {});
    });
    return () => {
      cancelled = true;
    };
  }, [key, tick]);

  return badges;
}
